import { ref } from 'vue';
import { isArray } from 'lodash';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function flatMapGenerator(data, rootKey = '') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let flatMap = {};
    Object.entries(data).forEach(([key, subData]) => {
        if (typeof (subData) === 'object') {
            if (rootKey === '') {
                flatMap[key] = flatMapGenerator(subData);
            }
            else {
                flatMap = { ...flatMap, ...flatMapGenerator(subData, key) };
            }
        }
        else if (isArray(subData)) {
            flatMap[key] = subData;
        }
        else {
            flatMap[key] = subData;
        }
    });
    if (rootKey !== '') {
        if (typeof (data[rootKey]) === 'object') {
            flatMap[rootKey] = true;
        }
    }
    return flatMap;
}
export default class ConfigurationManager {
    constructor({ configurationId, isConfigurationQueryParam, setConfigurationId, saveConfiguration, transferConfiguration, }) {
        this.configurationId = configurationId;
        this.isConfigurationQueryParam = isConfigurationQueryParam;
        this.setConfigurationId = setConfigurationId;
        this._saveConfiguration = saveConfiguration;
        this.transferConfiguration = transferConfiguration;
        this.hierarchy = ref(null);
        this.configuration = ref(null);
        this.prevNext = ref(null);
        this.configOwners = ref({ users: [], groups: [] });
    }
    async saveConfiguration(id, config) {
        const updateConfig = { ...this.configuration.value, ...config };
        this._saveConfiguration(id, updateConfig);
    }
    setHierarchy(data) {
        this.hierarchy.value = data;
    }
    setConfigOwners(data) {
        this.configOwners.value = data;
    }
    setPrevNext(data) {
        this.prevNext.value = data;
    }
    setConfiguration(data) {
        if (data) {
            this.configuration.value = data;
        }
    }
    getConfigurationSetting(key) {
        if (this.configuration.value?.general?.configurationSettings) {
            const { configurationSettings } = this.configuration.value.general;
            return (configurationSettings && configurationSettings[key]);
        }
        return true;
    }
    getFlatUISettingMap(addRoots = false) {
        if (this.configuration.value?.UISettings) {
            const { UISettings } = this.configuration.value;
            if (UISettings) {
                let flatIndex = {};
                Object.entries(UISettings).forEach(([subKey, val]) => {
                    if (typeof val === 'object') {
                        flatIndex = { ...flatIndex, ...flatMapGenerator(val) };
                        if (addRoots) {
                            flatIndex[subKey] = true;
                        }
                    }
                    else if (isArray(val)) {
                        flatIndex[subKey] = val;
                    }
                    else {
                        flatIndex[subKey] = val;
                    }
                });
                return flatIndex;
            }
        }
        return {};
    }
    getUISetting(key) {
        if (this.configuration.value?.UISettings) {
            const val = this.getFlatUISettingMap(true)[key];
            if (val !== undefined) {
                return val;
            }
        }
        return true;
    }
    setCustomUI(customUI) {
        if (this.configuration.value) {
            this.configuration.value.customUI = customUI;
        }
    }
    getUISettingValue(key) {
        if (this.configuration.value?.UISettings) {
            const { UISettings } = this.configuration.value;
            if (UISettings) {
                const flatIndex = {};
                Object.entries(UISettings).forEach(([subKey, val]) => {
                    if (typeof val === 'object') {
                        flatIndex[subKey] = val;
                    }
                    else {
                        flatIndex[subKey] = val;
                    }
                });
                if (flatIndex[key] !== undefined) {
                    return flatIndex[key];
                }
            }
        }
        return true;
    }
    setUISettings(key, val) {
        if (this.configuration.value && !this.configuration.value.UISettings) {
            this.configuration.value.UISettings = {
                UITopBar: true,
                UIToolBar: true,
                UISideBar: true,
                UIContextBar: true,
                UITrackDetails: true,
                UIControls: true,
                UITimeline: true,
                UIInteractions: true,
            };
        }
        if (this.configuration.value?.UISettings) {
            const { UISettings } = this.configuration.value;
            if (UISettings) {
                UISettings[key] = val;
            }
        }
    }
    setRootUISettings(val) {
        if (this.configuration.value?.UISettings) {
            this.configuration.value.UISettings = val;
        }
    }
    updateAction(val, index) {
        if (this.configuration.value && !this.configuration.value?.actions) {
            this.configuration.value.actions = [];
        }
        if (this.configuration.value?.actions) {
            const { actions } = this.configuration.value;
            if (actions[index]) {
                actions[index] = val;
            }
            else {
                actions.push(val);
            }
            this.configuration.value.actions = actions;
        }
    }
    removeAction(index) {
        if (this.configuration.value && !this.configuration.value?.actions) {
            this.configuration.value.actions = [];
        }
        if (this.configuration.value?.actions) {
            const { actions } = this.configuration.value;
            if (actions.length === 1) {
                this.configuration.value.actions = [];
            }
            else if (actions[index]) {
                const newActions = actions.splice(index, 1);
                this.configuration.value.actions = newActions;
            }
        }
    }
    updateShortcut(val, index) {
        if (!this.configuration.value) {
            this.configuration.value = {};
        }
        if (this.configuration.value && !this.configuration.value?.shortcuts) {
            this.configuration.value.shortcuts = [];
        }
        if (this.configuration.value?.shortcuts) {
            const { shortcuts } = this.configuration.value;
            if (shortcuts[index]) {
                shortcuts[index] = val;
            }
            else {
                shortcuts.push(val);
            }
            this.configuration.value.shortcuts = shortcuts;
        }
    }
    removeShortCut(index) {
        if (this.configuration.value && !this.configuration.value?.shortcuts) {
            this.configuration.value.shortcuts = [];
        }
        if (this.configuration.value?.shortcuts) {
            const { shortcuts } = this.configuration.value;
            if (shortcuts.length === 1) {
                this.configuration.value.shortcuts = [];
            }
            else if (shortcuts[index]) {
                const newShortcuts = shortcuts.splice(index, 1);
                this.configuration.value.shortcuts = newShortcuts;
            }
        }
    }
    updateFilterTimeline(val, index) {
        if (this.configuration.value && !this.configuration.value?.filterTimelines) {
            this.configuration.value.filterTimelines = [];
        }
        if (this.configuration.value?.filterTimelines) {
            const { filterTimelines } = this.configuration.value;
            if (filterTimelines && filterTimelines[index]) {
                filterTimelines[index] = val;
            }
            else {
                filterTimelines.push(val);
            }
            this.configuration.value.filterTimelines = filterTimelines;
        }
    }
    removeFilterTimeline(index) {
        if (this.configuration.value && !this.configuration.value?.filterTimelines) {
            this.configuration.value.filterTimelines = [];
        }
        if (this.configuration.value?.filterTimelines) {
            const { filterTimelines } = this.configuration.value;
            if (filterTimelines.length === 1) {
                this.configuration.value.filterTimelines = [];
            }
            else if (filterTimelines[index]) {
                const newFilterTimelines = filterTimelines.splice(index, 1);
                this.configuration.value.filterTimelines = newFilterTimelines;
            }
        }
    }
    updateTimelineDisplay(val, index) {
        if (this.configuration.value && !this.configuration.value?.timelineConfigs) {
            this.configuration.value.timelineConfigs = {
                maxHeight: 300,
                timelines: [],
            };
            this.configuration.value.timelineConfigs.timelines = [];
        }
        if (this.configuration.value?.timelineConfigs) {
            const { timelines } = this.configuration.value.timelineConfigs;
            if (timelines && timelines[index]) {
                timelines[index] = val;
            }
            else {
                timelines.push(val);
            }
            const timelineBase = this.configuration.value.timelineConfigs;
            timelineBase.timelines = timelines;
            this.configuration.value = { ...this.configuration.value, timelineConfigs: timelineBase };
        }
    }
    removeTimelineDisplay(index) {
        if (this.configuration.value && !this.configuration.value?.timelineConfigs) {
            this.configuration.value.timelineConfigs = {
                maxHeight: 300,
                timelines: [],
            };
            this.configuration.value.timelineConfigs.timelines = [];
        }
        if (this.configuration.value?.timelineConfigs) {
            const { timelines } = this.configuration.value.timelineConfigs;
            if (timelines.length === 1) {
                this.configuration.value.timelineConfigs.timelines = [];
                this.configuration.value.timelineConfigs.maxHeight = 175;
            }
            else if (timelines[index]) {
                timelines.splice(index, 1);
            }
        }
    }
}
